import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  OverlayDrawer,
  Radio,
  RadioGroup,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowDownUp, Check, Search, Trash2, Undo2, X } from 'lucide-react';

import RelativeTime from '../RelativeTime';
import { IDENTIFIER, NUMERIC, STROKE, SURFACE, TEXT } from '../../theme/tokens';
import { SuggestionMatchedOn, TenantUser } from '../../types/identity';
import { extractConsentError, isGraphError } from '../../api/identity';
import { IdentityWorkbench } from '../../hooks/useIdentityWorkbench';
import ConsentMessageBar from './ConsentMessageBar';
import MappingStatusChip from './MappingStatusChip';
import TenantUserSearchPanel from './TenantUserSearchPanel';
import {
  EXCEPTION_REASON_DETAIL,
  EXCEPTION_REASON_LABELS,
  IdentityDetail,
  deriveExceptionReason,
  MATCHED_ON_LABELS,
  deriveRowActions,
} from './identityRowModel';

// FD-509 — the inspector, and the surface an operator actually resolves an
// identity on. It replaces FD-495's read-only drawer.
//
// FD-517 widened it to 560px and made its footer a REAL WRITE (owner decision,
// 2026-09-28). Both changes come from the same finding: at 420px the manual
// search mounted a five-column results TABLE inside an overlay, so every cell
// wrapped and the panel scrolled sideways inside a panel. The search is now a
// name-over-UPN list (`TenantUserSearchPanel variant="list"` — one search
// implementation, two presentations), and the destination card carries its own
// match evidence and candidate chooser the way the mockup draws it.
//
// WHY A NON-MODAL OVERLAY (owner decision D1, 2026-09-27). The handoff's core
// requirement is to "investigate and resolve a selected identity WITHOUT losing
// position in the queue", and a modal drawer makes that structurally
// impossible: the queue behind it is inert, so the loop is close, click the
// next row, reopen. `modalType="non-modal"` keeps the table clickable and
// keeps the drawer floating over its right edge, which is also what the
// mockups draw. Clicking another row SWAPS the contents rather than closing.
//
// WHY "SAVE MAPPING" WRITES RATHER THAN STAGES. Three readings were costed and
// the owner chose the direct write. What keeps it from becoming a second
// implementation is that it goes through `wb.saveOne`, which calls the same
// `setIdentityMappings` / `removeIdentityMappings` with a one-element array and
// reads the same response buckets `handleSave` reads — so a freeze rejection
// arrives here as the server's own reason rather than as a second opinion about
// what the server would have said. The FD-ADR-036 shaping is still
// `deriveRowActions`, shared with the row's own cluster: there is no second
// derivation to drift.
//
// WHAT THE MOCKUP ASKS FOR THAT IS OMITTED, extending FD-ADR-043 D4 and
// R-FD-053 §9 rather than re-litigating them: Manager and Source groups (extra
// Graph calls per user — D3 took `department` and `jobTitle` because they were
// free on an existing $select, and these are not); a Confidence row (there is
// no score, and restating the match badge above it is the restatement FD-504
// removed from the check-in cell); the Details and Activity tabs (Details is
// every field already on screen, and Activity has NO producer — the mapping
// events are batch-level, written with SerialNumber = "SYSTEM", so nothing on
// the wire attributes one to one identity); and `View in Entra`, which would
// resolve against whichever tenant the operator's browser session is in — for a
// T2T campaign, usually not the destination.
//
// MOUNTED ALWAYS, `open` TOGGLED — a Fluent dialog surface mounted already-open
// never enters the accessibility tree (FLUENT_UI_ADDENDUM; a drawer is a
// dialog), and the failure is invisible on screen.

const useStyles = makeStyles({
  surface: {
    // Set through Fluent's OWN variable, not `width`: `OverlayDrawer` resolves
    // its width to `var(--fui-Drawer--size)`, so a `width` declaration here is
    // overwritten and the drawer silently keeps its `size` prop.
    '--fui-Drawer--size': '560px',
    maxWidth: '100vw',
  },
  /**
   * THE CARDS ONLY SEPARATE IF THE GROUND BEHIND THEM IS NOT ALSO WHITE (owner,
   * 2026-09-28). Every block in here is a white `Section` with a 1px `#DDE6F0`
   * border — which at 1.26:1 on white is a decorative divider by design
   * (`theme/tokens.ts`), so on a white drawer body the panel read as one flat
   * sheet with faint lines across it rather than as the stacked cards the
   * mockup draws.
   *
   * The fix is the ground, not a heavier border or a shadow: the console's rule
   * is that separation comes from strokes AND FILL STEPS, never shadows. So the
   * body takes the canvas and the cards keep their white — the same
   * canvas/card relationship every page in the console already uses.
   */
  body: {
    backgroundColor: SURFACE.canvas,
  },
  /** White chrome above and below the canvas, each with its own edge. */
  header: {
    backgroundColor: SURFACE.base,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: STROKE.base,
  },
  footerBar: {
    backgroundColor: SURFACE.base,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: STROKE.base,
  },
  /**
   * The search affordance is a REAL bordered control (owner, 2026-09-28). As a
   * transparent button it read as a line of blue text inside a grey card — the
   * mockup draws a full-width bordered row, and a control whose bounds are
   * invisible is the thing `STROKE.base`'s own doc comment warns against.
   */
  searchAffordance: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
    rowGap: '8px',
    width: '100%',
  },
  /** Pushes Cancel/Save to the trailing edge, past a leading Remove. */
  footerSpacer: {
    marginLeft: 'auto',
  },
  candidateRow: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  swap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '28px',
    height: '28px',
    marginLeft: 'auto',
    marginRight: 'auto',
    borderRadius: '999px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: STROKE.base,
    borderRightColor: STROKE.base,
    borderBottomColor: STROKE.base,
    borderLeftColor: STROKE.base,
    backgroundColor: SURFACE.base,
    color: TEXT.secondary,
  },
});

export interface IdentityInspectorProps {
  open: boolean;
  onClose: () => void;
  detail: IdentityDetail | null;
  wb: IdentityWorkbench;
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  destTenantDomain?: string | null;
  destTenantId?: string | null;
  /** Navigates to this campaign's devices — the blast radius, not a list. */
  onViewDevices?: () => void;
}

// The FD-103 SID classification, named rather than echoed raw. `unknown` is the
// one that costs something — it is why a row needs attention — so it says so.
const SID_TYPE_LABELS: Record<string, string> = {
  entraObjectId: 'Entra object ID, SID-encoded',
  onPremSid: 'On-premises SID',
  unknown: 'Unrecognised SID format',
};

/** What the last write came back as, for the bar above the footer. */
type WriteState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; message: string }
  | { kind: 'rejected'; reason: string }
  | { kind: 'error'; error: unknown };

export default function IdentityInspector({
  open,
  onClose,
  detail,
  wb,
  migrationId,
  migrationType,
  destTenantDomain,
  destTenantId,
  onViewDevices,
}: IdentityInspectorProps) {
  const styles = useStyles();

  return (
    <OverlayDrawer
      open={open}
      position="end"
      // The queue stays clickable while this is open — the whole point of the
      // surface. Mutation-testing this back to `modal` reddens its own test.
      modalType="non-modal"
      className={styles.surface}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      {detail ? (
        <InspectorContent
          key={detail.key}
          detail={detail}
          wb={wb}
          migrationId={migrationId}
          migrationType={migrationType}
          destTenantDomain={destTenantDomain}
          destTenantId={destTenantId}
          onViewDevices={onViewDevices}
          onClose={onClose}
        />
      ) : (
        <DrawerHeader>
          <DrawerHeaderTitle>Identity details</DrawerHeaderTitle>
        </DrawerHeader>
      )}
    </OverlayDrawer>
  );
}

/**
 * Keyed on `detail.key` by the caller, which is what resets the chosen
 * destination, the open search and the last write outcome when the operator
 * clicks a different row. Doing that with an effect instead would leave one
 * render showing the previous identity's choice under the new identity's name.
 */
function InspectorContent({
  detail,
  wb,
  migrationId,
  migrationType,
  destTenantDomain,
  destTenantId,
  onViewDevices,
  onClose,
}: {
  detail: IdentityDetail;
  wb: IdentityWorkbench;
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  destTenantDomain?: string | null;
  destTenantId?: string | null;
  onViewDevices?: () => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  const { row, mapping } = detail;

  const vm = wb.allRowVms.find((c) => c.key === detail.key);
  const actions = vm
    ? deriveRowActions(vm, wb.staged, { frozen: wb.frozen, isT2T: wb.isT2T })
    : null;

  const topCandidate = row?.suggestions?.[0] ?? null;
  const canChoose = actions?.kind === 'suggestion' && actions.approvable;

  // The operator's pick. Pre-set to the ranked top candidate ONLY where
  // choosing one would be an add — a mapped row under an active wave cannot be
  // re-pointed (FD-ADR-036), and pre-filling a choice that cannot be committed
  // is the 207 this surface exists to avoid.
  const [chosen, setChosen] = useState<TenantUser | null>(
    canChoose && topCandidate ? topCandidate.user : null,
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [write, setWrite] = useState<WriteState>({ kind: 'idle' });

  // A successful write invalidates the queries, so `detail` re-derives and the
  // row arrives back as MAPPED. Drop the now-committed pick so `dirty` reads
  // false and Save stops offering to write what is already on the server.
  useEffect(() => {
    if (write.kind === 'saved') setChosen(null);
  }, [write.kind]);

  const exceptionReason = vm ? deriveExceptionReason(vm) : null;
  const stagedEdit = wb.staged.get(detail.key);

  const destination = useMemo(() => {
    if (chosen) {
      // A pick that is still the server's top-ranked candidate, on a row with
      // no mapping, is reported as a CANDIDATE and not as the operator's
      // choice. The panel seeds that pick so Save is live on open, and calling
      // a seeded default "chosen" would overstate what the operator did — the
      // distinction the queue's own heading has always drawn: a candidate is
      // not a mapping.
      const isSeededDefault = !mapping && chosen.id === topCandidate?.user.id;
      return {
        displayName: chosen.displayName,
        upn: chosen.userPrincipalName,
        id: chosen.id,
        source: (isSeededDefault ? 'candidate' : 'chosen') as 'candidate' | 'chosen',
      };
    }
    if (detail.stagedDestination) {
      return { ...detail.stagedDestination, source: 'staged' as const };
    }
    if (mapping) {
      return {
        displayName: mapping.destDisplayName,
        upn: mapping.destUserPrincipalName,
        id: mapping.destUserId,
        source: 'mapping' as const,
      };
    }
    if (topCandidate) {
      return {
        displayName: topCandidate.user.displayName,
        upn: topCandidate.user.userPrincipalName,
        id: topCandidate.user.id,
        source: 'candidate' as const,
      };
    }
    return null;
  }, [chosen, detail.stagedDestination, mapping, topCandidate]);

  const dirty = Boolean(
    chosen && chosen.id !== mapping?.destUserId,
  );
  // A staged edit is committable on its own, which is how one row is taken out
  // of a batch without discarding the rest.
  const savableStaged = stagedEdit?.kind === 'set' ? stagedEdit : null;
  const saving = write.kind === 'saving';

  const handleSaveMapping = async () => {
    if (saving) return;
    setWrite({ kind: 'saving' });
    try {
      if (dirty && actions?.kind === 'suggestion' && chosen) {
        const submission = wb.buildSubmission(actions.row, chosen);
        if (!submission) {
          setWrite({
            kind: 'rejected',
            reason:
              'This source user could not be resolved in the source tenant, so there is nothing valid to submit.',
          });
          return;
        }
        const outcome = await wb.saveOne(detail.key, { kind: 'set', submission });
        applyOutcome(outcome, setWrite);
        return;
      }
      if (savableStaged) {
        const outcome = await wb.saveOne(detail.key, {
          kind: 'set',
          submission: savableStaged.submission,
        });
        applyOutcome(outcome, setWrite);
      }
    } catch (err) {
      setWrite({ kind: 'error', error: err });
    }
  };

  const handleRemoveMapping = async (sourceKey: string) => {
    if (saving) return;
    setWrite({ kind: 'saving' });
    try {
      const outcome = await wb.saveOne(detail.key, { kind: 'remove', sourceKey });
      applyOutcome(outcome, setWrite);
    } catch (err) {
      setWrite({ kind: 'error', error: err });
    }
  };

  const removeSourceKey =
    actions?.kind === 'orphanMapping'
      ? actions.removeSourceKey
      : actions?.kind === 'suggestion' && actions.removable
        ? (mapping?.sourceUserId ?? mapping?.sourceUserSid ?? '')
        : '';
  const removable =
    (actions?.kind === 'orphanMapping' && actions.removable) ||
    (actions?.kind === 'suggestion' && actions.removable);

  const subtitle =
    row?.resolvedSourceUser?.userPrincipalName ??
    row?.userName ??
    mapping?.sourceUserPrincipalName ??
    null;

  return (
    <>
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close identity details"
              icon={<X size={18} strokeWidth={1.5} />}
              onClick={onClose}
            />
          }
        >
          <span className="flex items-center gap-3">
            <Avatar name={detail.title} size={40} color="colorful" aria-hidden />
            <span className="flex min-w-0 flex-col">
              <span
                className="truncate text-[15px] font-semibold"
                style={{ color: TEXT.primary }}
              >
                {detail.title}
              </span>
              {subtitle && (
                <span className="truncate text-[12px]" style={{ color: TEXT.secondary }}>
                  {subtitle}
                </span>
              )}
            </span>
            <MappingStatusChip status={detail.status} />
          </span>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.body}>
        <div className="flex flex-col gap-4 pb-6">
          <SourceCard detail={detail} />

          <div className={styles.swap} aria-hidden>
            <ArrowDownUp size={14} strokeWidth={1.5} />
          </div>

          <DestinationCard
            detail={detail}
            wb={wb}
            actions={actions}
            destination={destination}
            chosen={chosen}
            setChosen={setChosen}
            canChoose={Boolean(canChoose)}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            migrationId={migrationId}
            migrationType={migrationType}
            destTenantDomain={destTenantDomain}
            destTenantId={destTenantId}
          />

          {exceptionReason && (
            <Section title="Exception">
              <Badge appearance="outline" color="warning">
                {EXCEPTION_REASON_LABELS[exceptionReason]}
              </Badge>
              <Note>{EXCEPTION_REASON_DETAIL[exceptionReason]}</Note>
            </Section>
          )}

          <Section title={`Affected devices (${detail.deviceCount})`}>
            {detail.serialNumbers.length > 0 ? (
              <>
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {detail.serialNumbers.map((sn) => (
                    <li
                      key={sn}
                      className="text-[12px]"
                      style={{ ...IDENTIFIER, color: TEXT.primary }}
                    >
                      {sn}
                    </li>
                  ))}
                </ul>
                {onViewDevices && (
                  <Button size="small" appearance="subtle" onClick={onViewDevices}>
                    View devices
                  </Button>
                )}
              </>
            ) : (
              <Note>
                No device has reported this identity, so nothing is waiting on this mapping.
              </Note>
            )}
          </Section>

          {mapping && (
            <Section title="Mapping history">
              <Field label="Created">
                <RelativeTime iso={mapping.createdAt} />
              </Field>
              <Field label="Last updated">
                <RelativeTime iso={mapping.updatedAt} />
              </Field>
              <Field label="Validated">
                <RelativeTime iso={mapping.validatedAt} />
              </Field>
              <Field label="Created by">{mapping.createdBy ?? '—'}</Field>
            </Section>
          )}
        </div>
      </DrawerBody>

      <DrawerFooter className={styles.footerBar}>
        <div className="flex w-full flex-col gap-2">
          <WriteBar write={write} />
          <div className={styles.footer}>
            {removable && removeSourceKey && (
              <Button
                appearance="subtle"
                disabled={saving}
                icon={<Trash2 size={16} strokeWidth={1.5} />}
                onClick={() => handleRemoveMapping(removeSourceKey)}
              >
                Remove mapping
              </Button>
            )}
            {stagedEdit && (
              <Button
                appearance="subtle"
                disabled={saving}
                icon={<Undo2 size={16} strokeWidth={1.5} />}
                onClick={() => wb.unstage(detail.key)}
              >
                Undo
              </Button>
            )}
            <span className={styles.footerSpacer} />
            <Button appearance="secondary" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={saving || (!dirty && !savableStaged)}
              icon={
                saving ? (
                  <Spinner size="tiny" />
                ) : (
                  <Check size={16} strokeWidth={1.5} />
                )
              }
              onClick={handleSaveMapping}
            >
              Save mapping
            </Button>
          </div>
        </div>
      </DrawerFooter>
    </>
  );
}

function applyOutcome(
  outcome: { kind: string; reason?: string },
  setWrite: (w: WriteState) => void,
) {
  switch (outcome.kind) {
    case 'accepted':
      setWrite({ kind: 'saved', message: 'Mapping saved.' });
      return;
    case 'updated':
      setWrite({ kind: 'saved', message: 'Mapping updated.' });
      return;
    case 'removed':
      setWrite({ kind: 'saved', message: 'Mapping removed.' });
      return;
    case 'notFound':
      setWrite({ kind: 'saved', message: 'No mapping existed for this user.' });
      return;
    default:
      setWrite({ kind: 'rejected', reason: outcome.reason ?? 'The server declined this row.' });
  }
}

/**
 * The outcome of the last write, stated where the button that caused it is.
 *
 * A REJECTION is an answer, not a failure — the request landed and the server
 * declined this row, which under an active wave is exactly the FD-ADR-036
 * per-row reject the queue would have shown inside a 207. A thrown error is a
 * failure to get an answer, and consent-class 409s get their own rendering
 * because the remedy is an admin consent run rather than a retry.
 */
function WriteBar({ write }: { write: WriteState }) {
  if (write.kind === 'idle' || write.kind === 'saving') return null;
  if (write.kind === 'saved') {
    return (
      <MessageBar intent="success">
        <MessageBarBody>{write.message}</MessageBarBody>
      </MessageBar>
    );
  }
  if (write.kind === 'rejected') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Not saved</MessageBarTitle>
          {write.reason}
        </MessageBarBody>
      </MessageBar>
    );
  }
  const consent = extractConsentError(write.error);
  if (consent) return <ConsentMessageBar consent={consent} />;
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>
          {isGraphError(write.error)
            ? 'Directory temporarily unavailable'
            : 'Could not save this mapping'}
        </MessageBarTitle>
        {isGraphError(write.error)
          ? 'Microsoft Graph did not respond while validating the mapping. Nothing was written — retry in a moment.'
          : write.error instanceof Error
            ? write.error.message
            : 'Unexpected error'}
      </MessageBarBody>
    </MessageBar>
  );
}

function SourceCard({ detail }: { detail: IdentityDetail }) {
  const { row, mapping } = detail;
  return (
    <Section title="Source identity">
      {row ? (
        <>
          <Field label="Name">{row.displayName ?? '—'}</Field>
          {/* The RESOLVED source user's UPN — the source tenant's own answer,
              and what the table's Source user column renders. Without it the
              drawer showed only `userName` (the device's report of the last
              interactive logon), so the two surfaces could name the same row
              differently. Absent on D2C, which has no source tenant. */}
          {row.resolvedSourceUser?.userPrincipalName && (
            <Field label="UPN">{row.resolvedSourceUser.userPrincipalName}</Field>
          )}
          <Field label="Signed-in user">{row.userName ?? '—'}</Field>
          <Field label="SAM account">{row.samName ?? '—'}</Field>
          {/* FD-506 widened the Graph $select. Rendered only when present:
              a user with no department set is not the same as a Manager that
              never sends the field. */}
          {row.resolvedSourceUser?.department && (
            <Field label="Department">{row.resolvedSourceUser.department}</Field>
          )}
          {row.resolvedSourceUser?.jobTitle && (
            <Field label="Job title">{row.resolvedSourceUser.jobTitle}</Field>
          )}
          <Field label="SID" mono>
            {row.sid}
          </Field>
          <Field label="SID type">{SID_TYPE_LABELS[row.sidType] ?? row.sidType}</Field>
          {row.resolvedSourceUser && (
            <Field label="Object ID" mono>
              {row.resolvedSourceUser.id}
            </Field>
          )}
        </>
      ) : mapping ? (
        <>
          {/* FD-387 — a saved mapping with no suggestions row behind it. The
              mapping carries its own source projection, which is all there
              is; saying "no device has reported this identity" is the fact
              that explains the empty Devices section below. */}
          <Field label="Name">{mapping.sourceDisplayName ?? '—'}</Field>
          <Field label="User principal name">{mapping.sourceUserPrincipalName ?? '—'}</Field>
          <Field label="SAM account">{mapping.sourceSamAccountName ?? '—'}</Field>
          {mapping.sourceUserSid && (
            <Field label="SID" mono>
              {mapping.sourceUserSid}
            </Field>
          )}
          {mapping.sourceUserId && (
            <Field label="Object ID" mono>
              {mapping.sourceUserId}
            </Field>
          )}
        </>
      ) : (
        <Note>Added manually — nothing is written until you save.</Note>
      )}
    </Section>
  );
}

function DestinationCard({
  detail,
  wb,
  actions,
  destination,
  chosen,
  setChosen,
  canChoose,
  searchOpen,
  setSearchOpen,
  migrationId,
  migrationType,
  destTenantDomain,
  destTenantId,
}: {
  detail: IdentityDetail;
  wb: IdentityWorkbench;
  actions: ReturnType<typeof deriveRowActions> | null;
  destination: {
    displayName: string | null;
    upn: string | null;
    id: string;
    source: 'chosen' | 'staged' | 'mapping' | 'candidate';
  } | null;
  chosen: TenantUser | null;
  setChosen: (u: TenantUser | null) => void;
  canChoose: boolean;
  searchOpen: boolean;
  setSearchOpen: (v: boolean) => void;
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  destTenantDomain?: string | null;
  destTenantId?: string | null;
}) {
  const styles = useStyles();
  const { row, mapping } = detail;
  const topCandidate = row?.suggestions?.[0] ?? null;

  // ONE heading, always "Destination identity" — the mockup's, and the owner's
  // (2026-09-28). It used to carry the state in parentheses, which made the
  // card's own name move under the operator ("Suggested destination" on the
  // rows most in need of a stable label).
  //
  // The property that phrasing protected is NOT dropped: a candidate is still
  // not a mapping, and the panel still says so — in the badge sitting beside
  // the destination user below, which is where the mockup puts it and which
  // states it next to the thing it is about rather than two lines above it.
  const heading = 'Destination identity';
  const stateBadge =
    destination?.source === 'candidate'
      ? { label: 'Recommended match', color: 'success' as const }
      : destination?.source === 'chosen'
        ? { label: 'Not yet saved', color: 'brand' as const }
        : destination?.source === 'staged'
          ? { label: 'Staged', color: 'informative' as const }
          : null;

  // The change affordance and its refusal reason come from `deriveRowActions`,
  // the SAME derivation the row's own cluster uses — so the freeze is presented
  // once and cannot disagree with itself.
  const changeDisabled = actions?.kind === 'suggestion' ? actions.changeDisabled : false;
  const changeDisabledReason =
    actions?.kind === 'suggestion' ? actions.changeDisabledReason : undefined;
  const searchable = actions?.kind === 'suggestion';

  return (
    <Section title={heading}>
      {detail.stagedRemoval ? (
        <Note>Removal staged — the mapping is deleted when you save.</Note>
      ) : destination ? (
        <>
          <div className="flex items-center gap-3">
            <Avatar name={destination.displayName ?? destination.upn ?? undefined} size={32} color="colorful" aria-hidden />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-medium" style={{ color: TEXT.primary }}>
                {destination.displayName ?? destination.upn ?? destination.id}
              </span>
              {destination.upn && (
                <span className="truncate text-[12px]" style={{ color: TEXT.secondary }}>
                  {destination.upn}
                </span>
              )}
            </span>
            {stateBadge && (
              <Badge appearance="tint" color={stateBadge.color}>
                {stateBadge.label}
              </Badge>
            )}
          </div>
          <Field label="Object ID" mono>
            {destination.id}
          </Field>
          {mapping && destination.source === 'mapping' && (
            <Field label="Delivery gate">
              {mapping.destinationReady === false ? (
                <Badge appearance="tint" color="warning">
                  Destination not ready
                </Badge>
              ) : mapping.destinationReady === true ? (
                <Badge appearance="tint" color="success">
                  Ready
                </Badge>
              ) : (
                // Additive field — a Manager deployed before FD-357 omits it,
                // and absent is not the same as false.
                <span style={{ color: TEXT.secondary }}>Not reported</span>
              )}
            </Field>
          )}

        </>
      ) : (
        <Note>No destination identity yet.</Note>
      )}

      {/* The mockup's "Match details", folded INTO the destination card rather
          than living as a sibling section below the candidate list: it is
          evidence ABOUT this destination, and a reader two cards away has to
          carry it back up.
          
          It renders whether or not there IS a destination, and that is not an
          oversight to tidy away. A needs-attention row has no destination by
          definition, and it is the row most in need of a sentence saying why —
          nesting this inside the `destination` branch silently deleted that
          explanation for exactly the rows that need it. */}
      <SubCard title="Match details">
        {row?.needsAttention ? (
          <Note>The source identity could not be resolved in the source tenant.</Note>
        ) : topCandidate ? (
          <>
            <Field label="Match type">
              <Badge appearance="outline" color="informative">
                {MATCHED_ON_LABELS[topCandidate.matchedOn as SuggestionMatchedOn] ??
                  topCandidate.matchedOn}
              </Badge>
            </Field>
            {topCandidate.user.department && (
              <Field label="Department">{topCandidate.user.department}</Field>
            )}
            {topCandidate.user.jobTitle && (
              <Field label="Job title">{topCandidate.user.jobTitle}</Field>
            )}
            {/* The candidate COUNT, as a fact. FD-511's refusal to call several
                candidates an "ambiguity" depends on this number being visible
                somewhere: the server ranks up to five and computes no ambiguity,
                so the count is what an operator gets instead of a verdict
                nobody produced. The chooser below only appears when there is
                more than one AND choosing is allowed, so it cannot carry it. */}
            <Field label="Candidates">
              <span style={NUMERIC}>{row?.suggestions.length ?? 0}</span>
            </Field>
            {/* The SERVER's bulk-accept verdict, rendered and never recomputed
                (FD-ADR-037 §2). It gates the unattended sweep and nothing else:
                an operator approving a displayed candidate is their own act. */}
            <Field label="Auto-accept eligible">
              {row?.autoAcceptEligible ? 'Yes' : 'No'}
            </Field>
          </>
        ) : mapping ? (
          <Note>
            Approved{mapping.createdBy ? ` by ${mapping.createdBy}` : ''} — the server
            re-resolved the destination user when it was written.
          </Note>
        ) : (
          <Note>No destination candidate was found for this identity.</Note>
        )}
      </SubCard>

      {/* FD-509 — every tier the server returned, choosable here. The server
          ranks and returns up to five; until FD-382 the UI rendered index 0 and
          discarded the rest, and the chooser that fixed it lives in a table
          cell. This is the same list at full width, with the UPN beside every
          name because two destination users can share a display name and be
          different people (the FD-505 scenario). */}
      {canChoose && row && row.suggestions.length > 1 && (
        <SubCard tone="panel" title={`Other candidates (${row.suggestions.length - 1})`}>
          <RadioGroup
            value={chosen?.id ?? topCandidate?.user.id ?? ''}
            onChange={(_, data) => {
              const pick = row.suggestions.find((s) => s.user.id === data.value);
              if (pick) setChosen(pick.user);
            }}
          >
            {row.suggestions.map((s) => (
              <div key={s.user.id} className={styles.candidateRow}>
                <Radio
                  value={s.user.id}
                  label={
                    <span className="flex flex-col">
                      <span className="text-[13px]" style={{ color: TEXT.primary }}>
                        {s.user.displayName ?? s.user.userPrincipalName ?? s.user.id}
                      </span>
                      {s.user.displayName && s.user.userPrincipalName && (
                        <span className="text-[12px]" style={{ color: TEXT.secondary }}>
                          {s.user.userPrincipalName}
                        </span>
                      )}
                      <span className="mt-0.5 flex flex-wrap items-center gap-1">
                        <Badge appearance="outline" color="informative">
                          {MATCHED_ON_LABELS[s.matchedOn as SuggestionMatchedOn] ?? s.matchedOn}
                        </Badge>
                        {s.user.accountEnabled === false && (
                          <Badge appearance="tint" color="warning">
                            Account disabled
                          </Badge>
                        )}
                      </span>
                    </span>
                  }
                />
              </div>
            ))}
          </RadioGroup>
        </SubCard>
      )}

      {/* The free search, behind a link rather than mounted open — and as a
          LIST, not the five-column table this panel used to embed. It is the
          destination tenant only: the source side is the add panel's job, and
          on D2C there is no source directory to search. */}
      {searchable &&
        (searchOpen ? (
          <SubCard
            tone="panel"
            title={
              migrationType === 'T2T' ? 'Search the destination tenant' : 'Search Entra'
            }
          >
            <TenantUserSearchPanel
              migrationId={migrationId}
              tenant="destination"
              tenantDomain={destTenantDomain}
              tenantId={destTenantId}
              variant="list"
              selectedUserId={chosen?.id ?? null}
              onSelect={(user) => {
                setChosen(user);
                setSearchOpen(false);
              }}
            />
          </SubCard>
        ) : (
          // DISABLED with its reason rather than withheld, which is how the
          // row's own cluster presents the same refusal — both read
          // `changeDisabled` / `changeDisabledReason` off the one
          // `deriveRowActions`. Withholding it here would make the freeze look
          // like two different rules depending on which surface you are on.
          <Button
            appearance="secondary"
            className={styles.searchAffordance}
            disabled={changeDisabled}
            title={changeDisabledReason}
            icon={<Search size={14} strokeWidth={1.5} />}
            onClick={() => setSearchOpen(true)}
          >
            Search for a different user
          </Button>
        ))}
      {actions?.kind === 'suggestion' && changeDisabled && changeDisabledReason && (
        <Note>{changeDisabledReason}</Note>
      )}
      {/* `wb` is read above through `actions`; referenced here so the prop is
          not mistaken for dead weight by a future reader. */}
      {wb.frozen && actions?.kind === 'orphanMapping' && (
        <Note>The wave is active, so this mapping cannot be changed.</Note>
      )}
    </Section>
  );
}

// --- layout primitives -------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="rounded-lg border px-3 py-3"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      <h3
        className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.06em]"
        style={{ color: TEXT.secondary }}
      >
        {title}
      </h3>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

/**
 * A card nested inside another — the mockup's inset panels. A real `<section>`
 * rather than a `<div>`, so `closest('section')` from its heading scopes to THIS
 * block: an assertion that has to say where a match tier appears cannot say it
 * against an unnamed wrapper.
 */
function SubCard({
  title,
  children,
  tone = 'inset',
}: {
  title: string;
  children: React.ReactNode;
  /**
   * `inset` is subordinate content — evidence ABOUT the card it sits in, on the
   * canvas fill so it reads as part of its parent. `panel` is a block the
   * operator ACTS on, on white so it reads as its own surface. The mockup draws
   * exactly that split: Match details grey, the candidate chooser and the
   * search white.
   */
  tone?: 'inset' | 'panel';
}) {
  return (
    <section
      className="mt-2 rounded-md border px-3 py-2"
      style={{
        borderColor: STROKE.base,
        backgroundColor: tone === 'panel' ? SURFACE.base : SURFACE.canvas,
      }}
    >
      <h4 className="m-0 mb-1.5 text-[11px] font-semibold" style={{ color: TEXT.secondary }}>
        {title}
      </h4>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[112px_minmax(0,1fr)] items-baseline gap-2">
      <span className="text-[12px]" style={{ color: TEXT.secondary }}>
        {label}
      </span>
      <span
        className="break-words text-[13px]"
        style={mono ? { ...IDENTIFIER, color: TEXT.primary } : { color: TEXT.primary }}
      >
        {children}
      </span>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 text-[12px]" style={{ color: TEXT.secondary }}>
      {children}
    </p>
  );
}
